import React, { useCallback, useEffect, useRef, useState } from "react";

import { fetchCartCount, fetchHeaderTheme, fetchPayingTransactionCount, fetchUser } from "../../lib/session";
import { storefrontUrl } from "../../lib/storefront";
import { DesktopAccount, MobileAccountDrawer } from "./AccountMenu";
import { DesktopCatalogSearch, MobileCatalogSearch } from "./CatalogSearch";
import { CartIcon, HistoryIcon, MenuIcon, SearchIcon, TransactionsIcon, WishlistIcon } from "./icons";
import UtilityStrip from "./UtilityStrip";
import "./SiteHeader.scss";

/** hanamaru public/static/img: Assets.KyouMascot, the logo when no campaign runs. */
const DEFAULT_LOGO = "https://kyoucdn.id/mascot/Mascot.webp";

const DESKTOP_QUERY = "(min-width: 1025px)";

/**
 * The kyou.id header, ported from hanamaru (`Navbar/index.tsx` plus the
 * `AppInstallPrompt` strip) so the help center wears the storefront's own
 * two-row header, as the visual handover requires.
 *
 * It replaces `Navbar2`, a 2019-era copy that had drifted: it read the login
 * from localStorage, which hanamaru no longer writes, so every visitor looked
 * logged out; it counted the cart by summing item quantities; and its search
 * box sent catalog searches into this app's own FAQ search.
 *
 * Interactive parts that depend on hanamaru internals are simplified, and each
 * is noted where it lives: the category mega menu becomes a link to the
 * catalog, the search has no typeahead, the scan button is left out (it needs
 * the camera flow), and the account menu only navigates.
 */
const SiteHeader = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);
  const [theme, setTheme] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const desktopSearchRef = useRef(null);

  useEffect(() => {
    let active = true;

    fetchHeaderTheme().then(result => {
      if (active) setTheme(result);
    });
    fetchUser().then(result => {
      if (!active) return;
      setUser(result);
      setIsLoading(false);
      if (result === null) return;

      fetchCartCount().then(count => active && setCartCount(count));
      fetchPayingTransactionCount().then(count => active && setTransactionCount(count));
    });

    return () => {
      active = false;
    };
  }, []);

  /*
   * Ctrl/Cmd+K, as on the storefront. On desktop it focuses the catalog box in
   * the bar; hanamaru opens a dialog there, but this box has no suggestions to
   * show in one. On phones it opens the full-screen search.
   */
  useEffect(() => {
    const onKeyDown = e => {
      if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) return;

      e.preventDefault();
      if (window.matchMedia(DESKTOP_QUERY).matches && desktopSearchRef.current) desktopSearchRef.current.focus();
      else setIsSearchOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const closeSearch = useCallback(() => setIsSearchOpen(false), []);
  const openMenu = () => setIsMenuOpen(true);
  const openSearch = () => setIsSearchOpen(true);

  const isDark = theme !== null && theme.tone === "dark";
  // Two custom properties rather than one background-image: an inline style
  // cannot hold a media query, and the phone and desktop art are different
  // compositions (same approach as hanamaru).
  const artStyle =
    theme === null
      ? undefined
      : {
          "--header-art": `url("${theme.art.mobile}")`,
          "--header-art-lg": `url("${theme.art.desktop}")`,
          backgroundColor: theme.fallbackColor || undefined
        };

  return (
    <header className={`site-header${isDark ? " site-header--dark" : ""}`}>
      <UtilityStrip />

      <nav className={`site-nav${theme === null ? "" : " site-nav--themed"}`} style={artStyle} aria-label="Navigasi utama">
        <button type="button" className="site-icon-btn site-nav__menu" onClick={openMenu} aria-label="Buka menu">
          <MenuIcon className="site-nav__menu-icon" />
        </button>

        <a className="site-nav__brand" href={storefrontUrl((theme && theme.href) || "/")} aria-label="Ke halaman utama Kyou">
          <img
            src={(theme && theme.logo) || DEFAULT_LOGO}
            alt=""
            className={[
              "site-nav__logo",
              theme && theme.logo ? "site-nav__logo--seasonal" : "site-nav__logo--mascot",
              theme && theme.mascot ? "site-nav__logo--desktop-only" : ""
            ].join(" ")}
            decoding="async"
          />
          {theme && theme.mascot ? (
            <picture className="site-nav__mascot">
              <source media="(min-width: 1024px)" srcSet={theme.mascot.desktop} />
              <img src={theme.mascot.mobile} alt="" decoding="async" />
            </picture>
          ) : null}
        </a>

        <a className="site-nav__categories" href={storefrontUrl("/search?page=1,40&sort=newest")}>
          <MenuIcon className="site-nav__categories-icon" />
          Kategori
        </a>

        <DesktopCatalogSearch ref={desktopSearchRef} />

        <div className="site-nav__icons">
          <button type="button" className="site-icon-btn site-nav__icon site-nav__search-toggle" onClick={openSearch} aria-label="Cari produk">
            <SearchIcon className="site-nav__glyph site-nav__glyph--brand" />
          </button>
          <a
            href={storefrontUrl("/user/cart")}
            className={`site-nav__icon${cartCount > 0 ? " site-badge" : ""}`}
            data-count={cartCount}
            aria-label="Ke keranjang"
          >
            <CartIcon className="site-nav__glyph" />
          </a>
          {transactionCount > 0 || (user && user.isPartner) ? (
            <a
              href={storefrontUrl("/user/transactions")}
              className={`site-nav__icon${transactionCount > 0 ? " site-badge" : ""}`}
              data-count={transactionCount}
              aria-label="Ke transaksi"
            >
              <TransactionsIcon className="site-nav__glyph site-nav__glyph--brand" />
            </a>
          ) : null}
          <a href={storefrontUrl("/user/history")} className="site-nav__icon" aria-label="Ke riwayat order">
            <HistoryIcon className="site-nav__glyph" />
          </a>
          <a href={storefrontUrl("/user/wishlist")} className="site-nav__icon site-nav__icon--desktop" aria-label="Ke wishlist">
            <WishlistIcon className="site-nav__glyph" />
          </a>
        </div>

        <DesktopAccount user={user} isLoading={isLoading} />
      </nav>

      <MobileAccountDrawer open={isMenuOpen} user={user} onClose={closeMenu} />
      <MobileCatalogSearch open={isSearchOpen} onClose={closeSearch} />
    </header>
  );
};

export default SiteHeader;
